import React, { useState, useEffect } from "react";
import styled from "styled-components";
import SubscriptionPlanCard from "../../Components/SubscriptionsComponents/SubscriptionPlanCard";
import { formatPlanPrice } from "../../helper/planHelper";
import CustomModal from "../../Components/Generic/CustomModal";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { showGenericError } from "../../helper/Notifications";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CARDS_PER_PAGE = 3;

const SubscriptionCards = ({
  purchaseSubscription,
  contactSales,
  annual = false,
  currency = "GBP",
  product,
}) => {
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [page, setPage] = useState(0);
  const [pendingPlan, setPendingPlan] = useState(null);
  const { getPlatformProducts, subscription } = useLicensingContext();
  const { selectedProject } = useProjectContext();

  useEffect(() => {
    if (!selectedProject?.id) return;
    let isMounted = true;
    setLoading(true);

    getPlatformProducts(
      selectedProject.id,
      (response) => {
        if (!isMounted) return;
        const products = response?.data?.products || [];
        const matched = products.find((p) => p.slug === product) || null;
        setProductData(matched);
        setLoading(false);
      },
      (error) => {
        if (!isMounted) return;
        console.error("Failed to load products:", error);
        showGenericError();
        setLoading(false);
      }
    );

    return () => { isMounted = false; };
  }, [selectedProject?.id, product]);

  useEffect(() => { setPage(0); }, [product, annual]);

  if (loading) return <LoaderComponent loading={true} />;
  if (!productData) return null;

  const targetInterval = annual ? "year" : "month";
  const plans = (productData.product_plans || [])
    .filter((p) => p.active && !p.hidden && p.default && p.interval === targetInterval)
    .sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));

  const contactPlans = productData.custom_contacts || [];
  const allCards = [...plans.map(p => ({ ...p, _type: 'plan' })), ...contactPlans.map(c => ({ ...c, _type: 'contact' }))];

  const totalPages = Math.ceil(allCards.length / CARDS_PER_PAGE);
  const visibleCards = allCards.slice(page * CARDS_PER_PAGE, page * CARDS_PER_PAGE + CARDS_PER_PAGE);
  const visibleCount = visibleCards.length;

  const getPriceForCurrency = (plan) => {
    const key = `price_${currency.toLowerCase()}`;
    return plan[key] ?? null;
  };

  const handleGetStarted = (card, planName) => {
    if (!subscription) {
      purchaseSubscription(card.id, currency);
      return;
    }
    const renewsOn = subscription.next_payment_date || subscription.current_period_end;
    setPendingPlan({
      id: card.id,
      planName,
      priceText: formatPlanPrice(getPriceForCurrency(card), currency),
      periodText: `${productData.seat_based ? "per seat / " : "/ "}${card.interval}`,
      renewsOnText: renewsOn
        ? new Date(renewsOn).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
        : null,
    });
  };

  return (
    <Content>
      <SliderWrapper>
        {totalPages > 1 && (
          <NavButton
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
            aria-label="Previous"
            $hidden={page === 0}
          >
            <ChevronLeft size={22} />
          </NavButton>
        )}

        <SubscriptionsContainer $count={visibleCount} $total={allCards.length}>
          {visibleCards.map((card, index) => {
            if (card._type === 'contact') {
              return (
                <SubscriptionPlanCard
                  key={`contact-${index}`}
                  planName={card.title || "Enterprise"}
                  subtitle={card.subtitle || null}
                  benefits={card.benefits || []}
                  price={null}
                  currency={currency}
                  interval={null}
                  isContactPlan={true}
                  handleContactSales={contactSales}
                />
              );
            }

            const meta = card.display_metadata || {};
            const benefits = meta.benefits || [];
            const planName = `${productData.name} ${meta.title || card.name}`;
            const subtitle = meta.subtitle || null;

            return (
              <SubscriptionPlanCard
                key={card.id}
                planName={planName}
                subtitle={subtitle}
                benefits={benefits}
                price={getPriceForCurrency(card)}
                currency={currency}
                interval={card.interval}
                isContactPlan={false}
                seatBased={productData.seat_based}
                handleUpgradeSubscription={() => handleGetStarted(card, planName)}
              />
            );
          })}
        </SubscriptionsContainer>

        {totalPages > 1 && (
          <NavButton
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            aria-label="Next"
            $hidden={page >= totalPages - 1}
          >
            <ChevronRight size={22} />
          </NavButton>
        )}
      </SliderWrapper>

      {totalPages > 1 && (
        <Dots>
          {Array.from({ length: totalPages }).map((_, i) => (
            <Dot key={i} $active={i === page} onClick={() => setPage(i)} />
          ))}
        </Dots>
      )}

      <Spacer />

      <CustomModal
        isOpen={!!pendingPlan}
        onOpenChange={(open) => { if (!open) setPendingPlan(null); }}
        title="Are you sure you want to subscribe to this plan?"
        buttons={[
          { text: "Cancel", variant: "secondary", onClick: () => setPendingPlan(null) },
          { text: "Subscribe", variant: "primary", action: () => purchaseSubscription(pendingPlan.id, currency) },
        ]}
      >
        {pendingPlan && (
          <ConfirmDetails>
            <ConfirmPlanName>{pendingPlan.planName}</ConfirmPlanName>
            {pendingPlan.priceText && (
              <ConfirmPrice>
                {pendingPlan.priceText} <span>{pendingPlan.periodText}</span>
              </ConfirmPrice>
            )}
            <ConfirmNote>
              This will be charged to the payment method on your current subscription
              {pendingPlan.renewsOnText ? ` and renew with it on ${pendingPlan.renewsOnText}.` : "."}
            </ConfirmNote>
          </ConfirmDetails>
        )}
      </CustomModal>
    </Content>
  );
};

export default SubscriptionCards;

const Spacer = styled.div`
  height: 20px;
`;

const ConfirmDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background: #F9FAFB;
  border-radius: 10px;
`;

const ConfirmPlanName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #181D27;
`;

const ConfirmPrice = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #101828;

  span {
    font-size: 13px;
    font-weight: 400;
    color: #667085;
  }
`;

const ConfirmNote = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: #667085;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding: 10px 0;

  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  border: 1px solid #E9EAEB;
  background: #FFFFFF;
  color: #7F56D9;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 4px rgba(10, 13, 18, 0.06);
  visibility: ${({ $hidden }) => $hidden ? 'hidden' : 'visible'};

  &:hover:not(:disabled) {
    background: #F4EBFF;
    border-color: #D6BBFB;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }
`;

const SubscriptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(
     ${({ $count }) => $count},
    minmax(200px, ${({ $count, $total }) => 
      $total <= 3 ? '380px' :
      $count <= 2 ? '380px' :
      '1fr'
    })
  );
  grid-template-rows: auto auto auto auto 1fr auto;
  min-height: 600px;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: 20px;

  @media (max-width: 1300px) {
    gap: 12px;
    min-height: 500px;
  }

  @media (max-width: 1024px) {
    min-height: 420px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: unset;
    min-height: unset;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
`;

const Dot = styled.button`
  width: ${({ $active }) => $active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${({ $active }) => $active ? '#7F56D9' : '#E9EAEB'};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: ${({ $active }) => $active ? '#7F56D9' : '#D6BBFB'};
  }
`;